import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiService } from "@/services/apiService";
import auraLogo from "@/assets/uma-aura-logo.svg";

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const AuthDialog = ({ open, onOpenChange, onSuccess }: AuthDialogProps) => {
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpID, setOtpID] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { login } = useAuth();

  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => { if (cooldownRef.current) clearTimeout(cooldownRef.current); };
  }, [resendCooldown]);

  const handleSendOTP = async () => {
    if (phoneNumber.length !== 10) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid 10-digit phone number",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const result = await apiService.sendOtp(phoneNumber);
    setIsLoading(false);

    if (result.success && result.data) {
      setOtpID(result.data.otpID);
      setResendCooldown(30);
      toast({
        title: "OTP Sent",
        description: `Verification code sent to +91 ${phoneNumber}`,
      });
      setStep("otp");
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to send OTP. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a valid 6-digit OTP",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const result = await apiService.verifyOtp(phoneNumber, otp, otpID);
    setIsLoading(false);

    if (result.success && result.data) {
      const { token, userId, phoneNumber: phone, isProfileUpdated, name, role } = result.data;
      const userRole = role || 'user'; // Default to 'user' if no role provided
      login(token, userId, phone, isProfileUpdated, name, userRole);

      toast({
        title: "Success",
        description: "Login successful!",
      });
      
      // Close dialog and reset
      onOpenChange(false);
      setTimeout(() => {
        setStep("phone");
        setPhoneNumber("");
        setOtp("");
        setOtpID("");
        onSuccess?.();
      }, 300);
    } else {
      toast({
        title: "Error",
        description: result.error || "Invalid OTP. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    const result = await apiService.sendOtp(phoneNumber);
    setIsLoading(false);

    if (result.success && result.data) {
      setOtpID(result.data.otpID);
      setResendCooldown(30);
      toast({
        title: "OTP Resent",
        description: `New verification code sent to +91 ${phoneNumber}`,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to resend OTP. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <img src={auraLogo} alt="Uma's Aura Logo" className="h-16 w-auto" />
          </div>
          <DialogTitle className="text-2xl text-center">
            {step === "phone" ? "Welcome Back" : "Verify OTP"}
          </DialogTitle>
          <p className="text-center text-muted-foreground text-sm">
            {step === "phone" 
              ? "Enter your phone number to continue" 
              : `Enter the 6-digit code sent to +91 ${phoneNumber}`}
          </p>
        </DialogHeader>

        {step === "phone" ? (
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Phone Number
              </label>
              <div className="flex gap-2">
                <div className="flex items-center justify-center bg-muted px-3 rounded-md border border-input">
                  <span className="text-sm font-medium">+91</span>
                </div>
                <Input
                  type="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  maxLength={10}
                  className="flex-1"
                />
              </div>
            </div>

            <Button
              className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 hover:opacity-90 text-white font-bold py-6"
              onClick={handleSendOTP}
              disabled={isLoading}
            >
              {isLoading ? "Sending..." : "Send OTP"}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={setOtp}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 hover:opacity-90 text-white font-bold py-6"
              onClick={handleVerifyOTP}
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify & Continue"}
            </Button>

            <div className="text-center space-y-2">
              <button
                onClick={handleResendOTP}
                disabled={resendCooldown > 0}
                className={`text-sm font-medium ${resendCooldown > 0 ? 'text-muted-foreground cursor-not-allowed' : 'text-primary hover:underline'}`}
              >
                {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
              </button>
              <br />
              <button
                onClick={() => setStep("phone")}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Change phone number
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
